import { useRef, useState } from "react";
import {
  CheckCircle2,
  CircleDot,
  History,
  MoreHorizontal,
  Reply,
  RotateCcw,
  Trash2,
} from "lucide-react";

import { ChatComposer, type ChatComposerHandle } from "@/components/chat/thread/ChatComposer";
import { ChatThreadRow } from "@/components/chat/thread/ChatThreadRow";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { UserAvatarUser } from "@/components/ui/user-avatar";
import type { MentionUser } from "@/domains/pm/components/issues/MentionTextarea";
import { formatCommentTimestamp } from "@/domains/pm/utils/panelDates";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { profileDisplayName } from "@/stores/documents/repository/mappers";
import type {
  DocumentComment,
  DocumentThread,
  DocumentThreadLinkedIssue,
  MentionAudienceUser,
} from "@/stores/documents/comments/types";
import { composerLabels, rootRowLabels, rowLabels, timestampLabels } from "./commentsLabels";
import { deriveMentionIds, mentionDisplayName } from "./mentionAudience";
import { isExcelFile } from "@/utils/previewUtils";
import { ThreadHistoryPopover } from "./ThreadHistoryPopover";

export interface DocumentThreadCardProps {
  thread: DocumentThread;
  currentUserId: number | null;
  currentUser: UserAvatarUser | null;
  /** Org data admin — may edit or delete anyone's comment. */
  canModifyData: boolean;
  /** The thread may be filed as an issue (has a project, and the viewer may manage PM there). */
  canCreateIssue: boolean;
  /** The document open in the viewer/panel — its version badge reads "Viewing". */
  viewingDocumentId: number;
  /** True when a page badge can scroll the viewer to the page. */
  canGoToPage: boolean;
  buildingId: number;
  audience: MentionAudienceUser[];
  mentionUsers: MentionUser[];
  mentionDisplayNames: string[];
  readOnly: boolean;
  busy: boolean;
  highlighted: boolean;
  onGoToPage: (page: number, documentId: number) => void;
  onReply: (thread: DocumentThread, body: string, mentionedUserIds: number[]) => Promise<unknown>;
  onEdit: (
    thread: DocumentThread,
    comment: DocumentComment,
    body: string,
    mentionedUserIds: number[]
  ) => Promise<unknown>;
  onDeleteReply: (thread: DocumentThread, comment: DocumentComment) => Promise<unknown>;
  onDeleteThread: (thread: DocumentThread) => Promise<unknown>;
  onResolve: (thread: DocumentThread) => Promise<unknown>;
  onReopen: (thread: DocumentThread) => Promise<unknown>;
  onCreateIssue: (thread: DocumentThread) => Promise<unknown>;
  onOpenIssue: (issue: DocumentThreadLinkedIssue) => void;
}

const noViewer = (): Promise<void> => Promise.resolve();

/** Brand-red outline — the one highlight treatment on these cards. */
const RED_OUTLINE = "border border-red-600 text-red-600";

/**
 * One Google-Docs-style comment card: page + version badges, the root comment,
 * its replies, and the footer — Mark as complete / Reopen, Create issue (or
 * the linked issue's chip), Reply — with a History popover in the overflow.
 *
 * Highlights are red outlines, never tinted backgrounds: the card being
 * scrolled to, the page badge, the version being viewed, the issue chip.
 */
export function DocumentThreadCard(props: DocumentThreadCardProps) {
  const { thread, readOnly, busy } = props;
  const { t, language } = useLanguage();
  const c = t.documents.comments;
  const stamps = timestampLabels(c, language);

  const [replyOpen, setReplyOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");
  const [confirmThreadDelete, setConfirmThreadDelete] = useState(false);
  const composerRef = useRef<ChatComposerHandle>(null);

  const resolved = thread.resolvedAt !== null;
  const isThreadAuthor = props.currentUserId != null && thread.createdById === props.currentUserId;
  const canDeleteThread = !readOnly && (isThreadAuthor || props.canModifyData);

  const authorName = (comment: DocumentComment) =>
    comment.author ? (profileDisplayName(comment.author) ?? comment.author.email) : c.unknownUser;

  const canEdit = (comment: DocumentComment) =>
    !readOnly &&
    props.currentUserId != null &&
    (comment.authorId === props.currentUserId || props.canModifyData);

  const startReply = (comment: DocumentComment) => {
    setReplyOpen(true);
    // Hand over the caret with the author mentioned, like the issue thread.
    const member = props.audience.find((u) => u.id === comment.authorId);
    const name = member ? mentionDisplayName(member) : authorName(comment);
    // The composer mounts on this render; append once it exists.
    queueMicrotask(() =>
      composerRef.current?.appendMention({ userId: member?.id, displayName: name })
    );
  };

  const saveEdit = async (comment: DocumentComment) => {
    const trimmed = editBody.trim();
    if (!trimmed) return;
    if (trimmed !== comment.body) {
      await props.onEdit(thread, comment, trimmed, deriveMentionIds(trimmed, props.audience));
    }
    setEditingId(null);
    setEditBody("");
  };

  const rowFor = (comment: DocumentComment, isRoot: boolean) => (
    <ChatThreadRow
      key={comment.id}
      anchorId={comment.id}
      author={comment.author}
      authorName={authorName(comment)}
      timestamp={formatCommentTimestamp(comment.createdAt, stamps)}
      edited={Boolean(comment.modifiedAt)}
      body={comment.body}
      mentionDisplayNames={props.mentionDisplayNames}
      mentionUsers={props.mentionUsers}
      buildingId={props.buildingId}
      projectId={thread.projectId ?? 0}
      openViewer={noViewer}
      labels={isRoot ? rootRowLabels(c) : rowLabels(c)}
      canEdit={canEdit(comment)}
      isEditing={editingId === comment.id}
      editBody={editBody}
      onEditBodyChange={setEditBody}
      onStartEdit={() => {
        setEditingId(comment.id);
        setEditBody(comment.body);
      }}
      onCancelEdit={() => {
        setEditingId(null);
        setEditBody("");
      }}
      onSaveEdit={() => void saveEdit(comment)}
      onReply={() => startReply(comment)}
      // The root's delete removes the whole thread (and its sub-issue).
      onDelete={
        isRoot
          ? canDeleteThread
            ? () => setConfirmThreadDelete(true)
            : undefined
          : canEdit(comment)
            ? () => void props.onDeleteReply(thread, comment)
            : undefined
      }
      readOnly={readOnly}
      highlighted={props.highlighted && isRoot}
    />
  );

  const isViewing = thread.documentId === props.viewingDocumentId;
  // A spreadsheet's anchor number counts sheets, not pages.
  const onSheet = isExcelFile(thread.documentFilename ?? "");

  return (
    <article
      data-thread-id={thread.id}
      className={cn(
        "rounded-lg border bg-background p-3 transition-colors",
        props.highlighted && "border-red-600",
        resolved && "text-muted-foreground"
      )}
    >
      {/* Badges + overflow */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {thread.pageNumber != null && (
          <button
            type="button"
            disabled={!props.canGoToPage}
            onClick={() => props.onGoToPage(thread.pageNumber as number, thread.documentId)}
            title={
              props.canGoToPage
                ? (onSheet ? c.goToSheet : c.goToPage).replace("{page}", String(thread.pageNumber))
                : undefined
            }
            className={cn(
              "rounded px-1.5 py-0.5 text-[11px] font-medium",
              RED_OUTLINE,
              props.canGoToPage ? "hover:bg-red-50" : "cursor-default"
            )}
          >
            {(onSheet ? c.sheet : c.page).replace("{page}", String(thread.pageNumber))}
          </button>
        )}
        {thread.documentVersion != null && (
          <span
            className={cn(
              "rounded border px-1.5 py-0.5 text-[11px] font-medium",
              isViewing ? RED_OUTLINE : "text-muted-foreground"
            )}
            title={isViewing ? c.viewingVersion : (thread.documentFilename ?? undefined)}
          >
            {c.version.replace("{version}", String(thread.documentVersion))}
            {isViewing && <span className="ml-1 font-normal">· {c.viewingVersion}</span>}
          </span>
        )}
        {resolved && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {thread.resolvedByName
              ? c.completedBy.replace("{name}", thread.resolvedByName)
              : c.completed}
            {thread.resolvedAt && (
              <span className="text-muted-foreground">
                · {formatCommentTimestamp(thread.resolvedAt, stamps)}
              </span>
            )}
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          {/* Outside the menu on purpose: a popover nested in a Radix menu inside
              the viewer dialog closed all three on one Escape. */}
          <ThreadHistoryPopover
            threadId={thread.id}
            issueId={thread.linkedIssue?.id ?? null}
            projectId={thread.projectId}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground"
              aria-label={c.historyAction}
              title={c.historyAction}
            >
              <History className="h-4 w-4" />
            </Button>
          </ThreadHistoryPopover>
          {canDeleteThread && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground"
                  aria-label={c.moreActions}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                  onSelect={() => setConfirmThreadDelete(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {c.deleteThread}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Root + replies */}
      <div className="space-y-3 text-foreground">
        {rowFor(thread.root, true)}
        {thread.replies.length > 0 && (
          <div className="space-y-3 border-l-2 border-red-600/40 pl-3">
            {thread.replies.map((r) => rowFor(r, false))}
          </div>
        )}
      </div>

      {thread.linkedIssue && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {c.issueBacked.replace("{number}", String(thread.linkedIssue.number))}
        </p>
      )}

      {/* Footer actions */}
      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-1">
          {resolved ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={busy}
              onClick={() => void props.onReopen(thread)}
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              {c.reopen}
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-emerald-700 hover:text-emerald-800"
              disabled={busy}
              onClick={() => void props.onResolve(thread)}
            >
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
              {c.markComplete}
            </Button>
          )}
          {thread.linkedIssue ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn("h-7 text-xs hover:bg-red-50", RED_OUTLINE)}
              onClick={() => props.onOpenIssue(thread.linkedIssue as DocumentThreadLinkedIssue)}
              title={thread.linkedIssue.title}
            >
              <CircleDot className="mr-1 h-3.5 w-3.5" />
              {c.linkedIssue.replace("{number}", String(thread.linkedIssue.number))}
            </Button>
          ) : (
            props.canCreateIssue && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={busy}
                onClick={() => void props.onCreateIssue(thread)}
              >
                <CircleDot className="mr-1 h-3.5 w-3.5" />
                {c.createIssue}
              </Button>
            )
          )}
          {!replyOpen && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto h-7 text-xs"
              onClick={() => setReplyOpen(true)}
            >
              <Reply className="mr-1 h-3.5 w-3.5" />
              {c.reply}
            </Button>
          )}
        </div>
      )}

      {!readOnly && replyOpen && (
        <ChatComposer
          handleRef={composerRef}
          buildingId={props.buildingId}
          projectId={thread.projectId ?? 0}
          currentUser={props.currentUser}
          mentionUsers={props.mentionUsers}
          attachments={false}
          onSend={(body, mentionedUserIds) => {
            void props.onReply(thread, body, mentionedUserIds).then(() => setReplyOpen(false));
          }}
          labels={composerLabels(c, c.replyPlaceholder)}
          className="mt-2"
        />
      )}

      <ConfirmDialog
        open={confirmThreadDelete}
        onOpenChange={setConfirmThreadDelete}
        onConfirm={() => void props.onDeleteThread(thread)}
        title={c.confirmDeleteThread}
        description={c.confirmDeleteThreadDescription}
        confirmText={c.deleteThread}
      />
    </article>
  );
}
